import React, { useState, useEffect, useMemo, useRef } from 'react';
import PrimaryButton from './PrimaryButton';
import OutlineButton from './OutlineButton';
import '../../css/checkbox.css'

/**
 * @type {CheckBoxListData[]}
 */
const empty = []

/**
 * Component for showing a list of checkboxes and a select all option
 *
 * @param {CheckBoxListProps} props
 * @returns {React.JSX.Element}
 */
function CheckBoxList({ title, listData = [], cancelCallback, saveCallback, disabled=false }) {

    const [selectAllClass, setSelectAllClass] = useState('qCheckBox')
    const [selectedEntries, setSelectedEntries] = useState(empty)

    const selectAllRef = useRef(null)

    const selectedEntryIDs = useMemo(()=>{
        return selectedEntries.map(e => e.identifier)
    }, [selectedEntries])

    /**
     * Handles adding a newly checked item to the selectedEntries Array
     * @param {CheckBoxListData} entry
     */
    const addToSelctedEntries = entry => setSelectedEntries(prevEntries => [...prevEntries, entry])

    /**
     * Handles removing a newly unchecked item from the selectedEntries Array
     * @param {CheckBoxListData} entry
     */
    const removeEntry = entry => setSelectedEntries(prevEntries => prevEntries.filter(e => e.identifier !== entry.identifier))

    /**
     * Updates the selected state of all the checkboxes.
     * @param {React.ChangeEvent<HTMLInputElement>} e ChangeEvent data
     */
    const handleSelectAll = (checked) => {
        if(disabled){
            return
        }

        if(checked){
            setSelectedEntries(listData)
            selectAllRef.current.checked = true
        } else {
            setSelectedEntries(empty)
            selectAllRef.current.checked = false
        }
    }

    useEffect(()=>{
        if(listData.length === selectedEntries.length && selectedEntries.length > 0){
            setSelectAllClass('qCheckBox')
            selectAllRef.current.checked = true
        } else if(selectedEntries.length > 0){
            setSelectAllClass('qCheckBoxDash')
        } else {
            setSelectAllClass('qCheckBox')
            selectAllRef.current.checked = false
        }
    }, [selectedEntries])

    const handleCancel = e => {
        if(disabled){
            return
        }

        setSelectedEntries(empty)
        selectAllRef.current.checked = false

        cancelCallback(e, selectedEntries)
    }

    return (
        <div style={{ width: '100%', height: '100%', paddingLeft: 20, paddingRight: 20, paddingTop: 16, paddingBottom: 16, position: 'relative', background: 'white', boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03)', borderRadius: 8, outline: '1px #F1F1F1 solid', outlineOffset: '-1px', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 32, display: 'inline-flex' }}>
            <div style={{ alignSelf: 'stretch', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 12, display: 'flex' }}>
                <div style={{ justifyContent: 'flex-start', alignItems: 'flex-end', gap: 4, display: 'inline-flex' }}>
                    <div style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#707070', fontSize: 14, fontFamily: 'Montserrat', fontWeight: '400', wordWrap: 'break-word' }}>{title}</div>
                </div>
                <div style={{ width: 126, height: 28, flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', display: 'flex' }}>
                    <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 4, display: 'inline-flex' }}>
                        <div style={{ width: 36, height: 36, position: 'relative' }}>
                            <div style={{ position: 'relative', borderRadius: 4, justifyContent: 'center', alignItems: 'center', display: 'inline-flex' }}>
                                <input ref={selectAllRef} className={selectAllClass} onChange={e => handleSelectAll(e.target.checked)} type='checkbox' disabled={disabled}></input>
                            </div>
                        </div>
                        <div onClick={_ => handleSelectAll(!selectAllRef.current.checked)} style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#2F2F2F', fontSize: 16, fontFamily: 'Montserrat', fontWeight: '400', wordWrap: 'break-word', cursor: 'pointer' }}>Select all</div>
                    </div>
                </div>
                <div style={{ maxHeight: 292, overflowY: 'scroll' ,alignSelf: 'stretch', paddingLeft: 30, paddingRight: 30, flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 16, display: 'flex' }}>
                    {listData.map((data, index)=>(
                        <div
                            key={`${title}_checkbox_list_${index}_${data.identifier}`}
                            style={{ height: 28, flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', display: 'flex' }}
                        >
                            <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 4, display: 'inline-flex' }}>
                                <div style={{ width: 36, height: 36, position: 'relative' }}>
                                    <div style={{ position: 'relative', borderRadius: 4, justifyContent: 'center', alignItems: 'center', display: 'inline-flex' }}>
                                        <input
                                            className='qCheckBox'
                                            type='checkbox'
                                            disabled={disabled}
                                            checked={selectedEntryIDs.includes(data.identifier)}
                                            onChange={e => e.target.checked ? addToSelctedEntries(data) : removeEntry(data)}
                                        />
                                    </div>
                                </div>
                                <div
                                    onClick={_ => disabled ? null : selectedEntryIDs.includes(data.identifier) ? removeEntry(data) : addToSelctedEntries(data)}
                                    style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic', color: '#2F2F2F', fontSize: 16, fontFamily: 'Montserrat', fontWeight: '400', wordWrap: 'break-word', cursor: 'pointer' }}
                                >
                                    {data.label}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div style={{ maxHeight: 40, alignSelf: 'stretch', justifyContent: 'space-between', alignItems: 'flex-start', display: 'inline-flex' }}>
                <OutlineButton
                    buttonLabel='Cancel'
                    clickHandler={handleCancel}
                    disabled={disabled}
                />
                <PrimaryButton
                    buttonLabel='Save'
                    clickHandler={e => saveCallback(e, selectedEntries)}
                    disabled={disabled || selectedEntries.length === 0}
                />
            </div>
        </div>
    )
}

export default CheckBoxList